<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { AdminApiClient, AdminApiError, getAdminErrorMessage, loginWithSharedCode } from './api';
import { getOrderStatusLabel, normalizeAdminOrder, orderMatchesFilter, packageStatusLabel, type OrderFilter, type OrderFilterInput } from './admin-model';
import type { PackageStatus } from './package-model';
import { buildTencentMapScriptUrl, isValidCoordinate } from './map-picker';
import { getCropPreset } from './image-crop';
import { validateImageFile } from './image-upload';
import './package-description.css';
import { buildStoreImagePatch, type StoreImagePurpose } from './store-image';
import ImageCropDialog from './ImageCropDialog.vue';
import OrderDetailDrawer from './OrderDetailDrawer.vue';

type Tab = 'overview' | 'packages' | 'orders' | 'audit' | 'settings';

interface AdminPackage {
  id?: string;
  name: string;
  detail: string;
  price: number;
  status: string;
  rawStatus?: PackageStatus;
  coverUrl?: string;
  contentGroups?: Array<{ name: string; items: Array<{ name: string; quantity: number | string; unit: string; note?: string }> }>;
  purchaseRules?: Record<string, unknown>;
  validityDays?: number;
  publishedAt?: string | null;
  archivedAt?: string | null;
  canDelete?: boolean;
}

interface StoreProfile {
  name: string;
  address: string;
  phone: string;
  businessHours: string;
  notice: string;
  coverUrl: string;
  logoUrl: string;
  latitude?: number;
  longitude?: number;
}

type EditablePurchaseRules = {
  validity: string;
  availableTime: string;
  reservation: string;
  usage: string;
  refund: string;
  other?: string;
};

interface AdminOrder {
 id: string;
 sourceId?: string | null;
 buyer: string;
 item: string;
 amount: number;
 status: string;
 rawStatus?: string;
 time: string;
 quantity?: number;
 totalAmountFen?: number;
 createdAt?: string | null;
 paidAt?: string | null;
 packageSnapshot?: Record<string, unknown>;
 activeVoucherCount?: number;
 voucherCount?: number;
}

interface AdminReport {
  paidOrderCount: number;
  grossAmountFen: number;
  voucherCount: number;
  activeVoucherCount: number;
  redeemedVoucherCount: number;
  refundedVoucherCount: number;
}

interface IntegrationStatus {
  payment: { mode: string; ready: boolean };
  persistence: { mode: string };
  worker: { enabled: boolean };
}

const activeTab = ref<Tab>('overview');
const showPackageForm = ref(false);
const editingPackageId = ref<string | undefined>();
const packageView = ref<'ACTIVE' | 'ARCHIVED'>('ACTIVE');
const orderFilter = ref<OrderFilter>('ALL');
const orderSearch = ref('');
const orderLoading = ref(false);
const orderError = ref('');
const selectedOrder = ref<Record<string, any> | null>(null);
const selectedOrderVouchers = ref<Record<string, any>[]>([]);
const orderDetailLoading = ref(false);
const orderDetailError = ref('');
const cropFile = ref<File | null>(null);
const cropPurpose = ref<'store-cover' | 'store-logo' | 'package-cover' | null>(null);
const tenantName = '小贝壳海鲜啤酒屋';
const tabs: Array<{ id: Tab; label: string; icon: string }> = [
  { id: 'overview', label: '经营概览', icon: '⌂' },
  { id: 'packages', label: '团购套餐', icon: '◈' },
  { id: 'orders', label: '订单与退款', icon: '▣' },
  { id: 'audit', label: '审计日志', icon: '≡' },
  { id: 'settings', label: '接入设置', icon: '⚙' },
];

const packages = ref<AdminPackage[]>([]);
const orders = ref<AdminOrder[]>([]);
const auditLogs = ref<Array<{ id: string; action: string; targetId: string; createdAt: string }>>([]);
const report = ref<AdminReport | null>(null);
const integrationStatus = ref<IntegrationStatus>({ payment: { mode: 'unknown', ready: false }, persistence: { mode: 'unknown' }, worker: { enabled: false } });
const DEFAULT_PACKAGE_RULES = {
  validity: '购买后 28 天内有效',
  availableTime: '门店营业时间内可用',
  reservation: '无需预约，高峰期建议提前联系门店',
  usage: '到店出示团购券，核销后使用',
  refund: '未核销可退，已核销不可退',
  other: '',
};
const createDefaultPackageRules = (): EditablePurchaseRules => ({ ...DEFAULT_PACKAGE_RULES });
function toEditablePurchaseRules(value: unknown): EditablePurchaseRules {
  const source = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const defaults = createDefaultPackageRules();
  return {
    validity: typeof source.validity === 'string' ? source.validity : defaults.validity,
    availableTime: typeof source.availableTime === 'string' ? source.availableTime : defaults.availableTime,
    reservation: typeof source.reservation === 'string' ? source.reservation : defaults.reservation,
    usage: typeof source.usage === 'string' ? source.usage : defaults.usage,
    refund: typeof source.refund === 'string' ? source.refund : defaults.refund,
    ...(typeof source.other === 'string' ? { other: source.other } : {}),
  };
}
const newPackage = ref({ name: '', detail: '', price: '' });
const newPackageCoverUrl = ref('');
const newPackageValidityDays = ref('30');
const newPackageContentGroups = ref<NonNullable<AdminPackage['contentGroups']>>([]);
const newPackageRules = ref<EditablePurchaseRules>(createDefaultPackageRules());
const editingPackageHasStructuredDetails = ref(false);
const storeProfile = ref<StoreProfile>({ name: '', address: '', phone: '', businessHours: '', notice: '', coverUrl: '', logoUrl: '' });
const storeSaving = ref(false);
const packageSaving = ref(false);
const uploadState = ref('');
const syncState = ref('未登录后台');
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://deal-api.qunyu.site/api';
const configuredTenantId = import.meta.env.VITE_TENANT_ID || 'xiaobeike';
const storedSessionToken = typeof localStorage !== 'undefined' ? localStorage.getItem('shellfish_admin_session_token') || '' : '';
const apiClient = new AdminApiClient({ baseUrl: apiBaseUrl, tenantId: configuredTenantId, sessionToken: storedSessionToken || undefined });
const authenticated = ref(Boolean(storedSessionToken));

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}
const toasts = ref<Toast[]>([]);
let toastIdCounter = 0;
function showToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  const id = ++toastIdCounter;
  toasts.value.push({ id, message, type });
  setTimeout(() => {
    toasts.value = toasts.value.filter((t) => t.id !== id);
  }, 3500);
}

interface ConfirmDialogState {
  show: boolean;
  title: string;
  message: string;
  confirmText: string;
  danger: boolean;
  onConfirm: () => void | Promise<void>;
}
const confirmDialog = ref<ConfirmDialogState>({
  show: false,
  title: '',
  message: '',
  confirmText: '确定',
  danger: false,
  onConfirm: () => {},
});

function openConfirm(options: { title?: string; message: string; confirmText?: string; danger?: boolean; onConfirm: () => void | Promise<void> }) {
  confirmDialog.value = {
    show: true,
    title: options.title || '确认操作',
    message: options.message,
    confirmText: options.confirmText || '确定',
    danger: options.danger ?? true,
    onConfirm: options.onConfirm,
  };
}

function handleConfirmExecute() {
  const action = confirmDialog.value.onConfirm;
  confirmDialog.value.show = false;
  void action();
}
const authCode = ref('');
const authError = ref('');
const authSubmitting = ref(false);
const tencentMapKey = String(import.meta.env.VITE_TENCENT_MAP_KEY || '');
const mapContainer = ref<HTMLElement | null>(null);
const mapSearchAddress = ref('');
const mapStatus = ref('');
const mapLoading = ref(false);
let tencentMapPromise: Promise<any> | null = null;
let tencentMap: any;
let tencentMarker: any;
let tencentMapClickListener: (() => void) | undefined;
let tencentMarkerDragListener: (() => void) | undefined;

const visiblePackages = computed(() => packages.value.filter((item) => packageView.value === 'ARCHIVED' ? Boolean(item.archivedAt) : !item.archivedAt));
const visibleOrders = computed(() => {
  const search = orderSearch.value.trim().toLowerCase();
  return orders.value.filter((order) => {
    if (!orderMatchesFilter(order, orderFilter.value)) return false;
    if (!search) return true;
    return String(order.id ?? '').toLowerCase().includes(search) || String(order.buyer ?? '').toLowerCase().includes(search);
  });
});
const cropPreset = computed(() => cropPurpose.value ? getCropPreset(cropPurpose.value) : getCropPreset('package-cover'));
const cropAspectRatio = computed(() => cropPreset.value.aspectRatio);
const cropOutputWidth = computed(() => cropPreset.value.outputWidth);
const cropOutputHeight = computed(() => cropPreset.value.outputHeight);
const cropOutputMimeType = computed(() => cropPreset.value.mimeType);

const currentTitle = computed(() => tabs.find((tab) => tab.id === activeTab.value)?.label ?? '经营概览');
const metricSummary = computed(() => {
  const paidOrders = orders.value.filter((order) => ['PAID', 'PARTIALLY_REDEEMED', 'FULLY_REDEEMED', 'PARTIALLY_REFUNDED', 'REFUNDED'].includes(order.rawStatus ?? order.status));
  const amount = paidOrders.reduce((sum, order) => sum + order.amount, 0);
  const pendingVouchers = orders.value.filter((order) => ['PAID', 'PARTIALLY_REDEEMED'].includes(order.rawStatus ?? order.status)).length;
  return {
    amount: report.value ? report.value.grossAmountFen / 100 : amount,
    orderCount: report.value?.paidOrderCount ?? paidOrders.length,
    pendingVouchers: report.value?.activeVoucherCount ?? pendingVouchers,
  };
});

const DEFAULT_MAP_CENTER = { latitude: 39.9042, longitude: 116.4074 };

function loadTencentMapSdk(): Promise<any> {
  if (!tencentMapKey) return Promise.reject(new Error('TENCENT_MAP_KEY_MISSING'));
  const existing = (globalThis as any).TMap;
  if (existing) return Promise.resolve(existing);
  if (tencentMapPromise) return tencentMapPromise;
  tencentMapPromise = new Promise((resolve, reject) => {
    const scriptId = 'shellfish-tencent-map-sdk';
    const current = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (current) {
      current.addEventListener('load', () => resolve((globalThis as any).TMap));
      current.addEventListener('error', () => reject(new Error('TENCENT_MAP_LOAD_FAILED')));
      return;
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.src = buildTencentMapScriptUrl(tencentMapKey);
    script.onload = () => (globalThis as any).TMap ? resolve((globalThis as any).TMap) : reject(new Error('TENCENT_MAP_LOAD_FAILED'));
    script.onerror = () => reject(new Error('TENCENT_MAP_LOAD_FAILED'));
    document.head.appendChild(script);
  });
  return tencentMapPromise;
}

function currentMapCenter() {
  return isValidCoordinate(storeProfile.value.latitude, storeProfile.value.longitude)
    ? { latitude: storeProfile.value.latitude, longitude: storeProfile.value.longitude }
    : DEFAULT_MAP_CENTER;
}

function applyMapPosition(position: any) {
  const latitude = Number(position?.lat ?? position?.latitude);
  const longitude = Number(position?.lng ?? position?.longitude);
  if (!isValidCoordinate(latitude, longitude)) {
    mapStatus.value = '地图返回了无效坐标，请改为手动填写';
    return;
  }
  storeProfile.value.latitude = latitude;
  storeProfile.value.longitude = longitude;
  if (tencentMarker?.updateGeometries) {
    tencentMarker.updateGeometries([{ id: 'store', position: new (globalThis as any).TMap.LatLng(latitude, longitude), styleId: 'marker', properties: { title: '门店' }, draggable: true }]);
  }
  if (tencentMap?.setCenter) tencentMap.setCenter(new (globalThis as any).TMap.LatLng(latitude, longitude));
  mapStatus.value = '已选择门店位置，请点击“保存资料”';
}

function destroyTencentMap() {
  if (tencentMap && tencentMapClickListener) tencentMap.off?.('click', tencentMapClickListener);
  if (tencentMarker && tencentMarkerDragListener) tencentMarker.off?.('dragend', tencentMarkerDragListener);
  tencentMapClickListener = undefined;
  tencentMarkerDragListener = undefined;
  tencentMarker?.destroy?.();
  tencentMap?.destroy?.();
  tencentMarker = undefined;
  tencentMap = undefined;
}

async function initializeTencentMap() {
  if (activeTab.value !== 'settings' || !mapContainer.value) return;
  if (!tencentMapKey) {
    mapStatus.value = '腾讯地图未配置，可直接填写经纬度';
    return;
  }
  mapLoading.value = true;
  try {
    const TMap = await loadTencentMapSdk();
    if (!mapContainer.value || activeTab.value !== 'settings') return;
    destroyTencentMap();
    const center = currentMapCenter();
    const centerLatLng = new TMap.LatLng(center.latitude, center.longitude);
    tencentMap = new TMap.Map(mapContainer.value, { center: centerLatLng, zoom: 16 });
    tencentMarker = new TMap.MultiMarker({
      map: tencentMap,
      styles: { marker: new TMap.MarkerStyle({ width: 25, height: 35, anchor: { x: 12, y: 35 } }) },
      geometries: [{ id: 'store', position: centerLatLng, styleId: 'marker', properties: { title: '门店' }, draggable: true }],
    });
    tencentMapClickListener = (event: any) => applyMapPosition(event?.latLng);
    tencentMarkerDragListener = (event: any) => applyMapPosition(event?.geometry?.position ?? event?.latLng);
    tencentMap.on?.('click', tencentMapClickListener);
    tencentMarker.on?.('dragend', tencentMarkerDragListener);
    mapStatus.value = isValidCoordinate(storeProfile.value.latitude, storeProfile.value.longitude)
      ? '可点击地图或拖动标记调整门店位置'
      : '请搜索地址、点击地图或拖动标记选择真实门店位置';
  } catch (error) {
    mapStatus.value = getAdminErrorMessage(error);
  } finally {
    mapLoading.value = false;
  }
}

async function searchStoreAddress() {
  const address = mapSearchAddress.value.trim() || storeProfile.value.address.trim();
  if (!address) {
    mapStatus.value = '请先输入门店地址';
    return;
  }
  if (!tencentMapKey || !tencentMap) {
    mapStatus.value = '腾讯地图未配置，可直接填写经纬度';
    return;
  }
  try {
    const TMap = (globalThis as any).TMap;
    const result = await new TMap.service.Geocoder().getLocation({ address });
    const location = result?.result?.location ?? result?.result?.[0]?.location;
    if (!location) throw new Error('MAP_ADDRESS_NOT_FOUND');
    applyMapPosition(location);
  } catch {
    mapStatus.value = '没有找到这个地址，请点击地图或手动填写经纬度';
  }
}

async function signIn() {
  if (authSubmitting.value || !authCode.value.trim()) {
    authError.value = '请输入统一口令';
    return;
  }
  authSubmitting.value = true;
  authError.value = '';
  try {
    const session = await loginWithSharedCode(apiBaseUrl, authCode.value.trim());
    if (session.tenantId !== configuredTenantId) throw new Error('TENANT_SCOPE');
    apiClient.setSessionToken(session.sessionToken);
    localStorage.setItem('shellfish_admin_session_token', session.sessionToken);
    authenticated.value = true;
    authCode.value = '';
    await loadRemoteData();
  } catch (error) {
    authError.value = error instanceof AdminApiError && error.code === 'SHARED_CODE_RATE_LIMITED' ? '尝试次数过多，请稍后再试' : getAdminErrorMessage(error);
  } finally {
    authSubmitting.value = false;
  }
}

function signOut() {
  localStorage.removeItem('shellfish_admin_session_token');
  apiClient.setSessionToken(undefined);
  authenticated.value = false;
 packages.value = [];
 orders.value = [];
 orderError.value = '';
 orderLoading.value = false;
  report.value = null;
  integrationStatus.value = { payment: { mode: 'unknown', ready: false }, persistence: { mode: 'unknown' }, worker: { enabled: false } };
}

function openNewPackage() {
  editingPackageId.value = undefined;
  editingPackageHasStructuredDetails.value = true;
  packageView.value = 'ACTIVE';
  newPackage.value = { name: '', detail: '', price: '' };
  newPackageCoverUrl.value = '';
  newPackageValidityDays.value = '30';
  newPackageContentGroups.value = [{ name: '', items: [{ name: '', quantity: 1, unit: '份', note: '' }] }];
  newPackageRules.value = createDefaultPackageRules();
  showPackageForm.value = true;
}

function openEditPackage(item: AdminPackage) {
  if (!item.id) return;
  editingPackageId.value = item.id;
  editingPackageHasStructuredDetails.value = item.contentGroups !== undefined || item.purchaseRules !== undefined;
  newPackage.value = { name: item.name, detail: item.detail, price: String(item.price) };
  newPackageCoverUrl.value = item.coverUrl ?? '';
  newPackageValidityDays.value = String(item.validityDays ?? 28);
  newPackageContentGroups.value = item.contentGroups?.map((group) => ({ name: group.name, items: group.items.map((entry) => ({ ...entry })) })) ?? [];
  newPackageRules.value = toEditablePurchaseRules(item.purchaseRules);
  showPackageForm.value = true;
}

function packageStatusForApi(item: AdminPackage): PackageStatus {
  return item.rawStatus ?? (item.status === '已上架' ? 'PUBLISHED' : item.status === '已下架' ? 'OFF_SHELF' : 'DRAFT');
}

async function deletePackage(item: AdminPackage) {
  if (!item.id || !item.canDelete) return;
  openConfirm({
    title: '确认删除草稿套餐',
    message: `确定要删除草稿套餐「${item.name}」吗？删除后不可恢复。`,
    confirmText: '确认删除',
    danger: true,
    onConfirm: async () => {
      try {
        await apiClient.deletePackage(item.id!);
        syncState.value = '套餐已删除';
        showToast('草稿套餐已成功删除', 'success');
        await loadRemoteData();
      } catch (error) {
        const errMsg = `套餐删除失败：${getAdminErrorMessage(error)}`;
        syncState.value = errMsg;
        showToast(errMsg, 'error');
      }
    },
  });
}

async function archivePackage(item: AdminPackage) {
  if (!item.id) return;
  openConfirm({
    title: '确认归档套餐',
    message: `确定要归档套餐「${item.name}」吗？归档后套餐将自动下架。`,
    confirmText: '确认归档',
    danger: true,
    onConfirm: async () => {
      try {
        await apiClient.archivePackage(item.id!);
        syncState.value = '套餐已归档';
        showToast('套餐已归档并下架', 'success');
        await loadRemoteData();
      } catch (error) {
        const errMsg = `套餐归档失败：${getAdminErrorMessage(error)}`;
        syncState.value = errMsg;
        showToast(errMsg, 'error');
      }
    },
  });
}

async function restorePackage(item: AdminPackage) {
  if (!item.id) return;
  try {
    await apiClient.restorePackage(item.id);
    syncState.value = '套餐已恢复为下架状态';
    showToast('套餐已恢复为下架状态', 'success');
    await loadRemoteData();
  } catch (error) {
    const errMsg = `套餐恢复失败：${getAdminErrorMessage(error)}`;
    syncState.value = errMsg;
    showToast(errMsg, 'error');
  }
}

async function togglePackage(item: AdminPackage) {
  if (!item.id) {
    syncState.value = '套餐尚未同步，暂不能修改状态';
    showToast('套餐尚未同步，暂不能修改状态', 'info');
    return;
  }
  const nextStatus: PackageStatus = packageStatusForApi(item) === 'PUBLISHED' ? 'OFF_SHELF' : 'PUBLISHED';
  try {
    await apiClient.updatePackageStatus(item.id, nextStatus);
    item.rawStatus = nextStatus;
    item.status = nextStatus === 'PUBLISHED' ? '已上架' : '已下架';
    const msg = nextStatus === 'PUBLISHED' ? '套餐已成功上架' : '套餐已下架';
    syncState.value = msg;
    showToast(msg, 'success');
  } catch (error) {
    const errMsg = `状态变更失败：${getAdminErrorMessage(error)}`;
    syncState.value = errMsg;
    showToast(errMsg, 'error');
  }
}

function addContentGroup() {
  newPackageContentGroups.value.push({ name: '', items: [{ name: '', quantity: 1, unit: '份', note: '' }] });
}

function removeContentGroup(index: number) {
  newPackageContentGroups.value.splice(index, 1);
}

function moveContentGroup(index: number, direction: -1 | 1) {
  const next = index + direction;
  if (next < 0 || next >= newPackageContentGroups.value.length) return;
  const groups = newPackageContentGroups.value;
  [groups[index], groups[next]] = [groups[next], groups[index]];
}

function addContentItem(groupIndex: number) {
  newPackageContentGroups.value[groupIndex]?.items.push({ name: '', quantity: 1, unit: '份', note: '' });
}

function removeContentItem(groupIndex: number, itemIndex: number) {
  newPackageContentGroups.value[groupIndex]?.items.splice(itemIndex, 1);
}

function moveContentItem(groupIndex: number, itemIndex: number, direction: -1 | 1) {
  const items = newPackageContentGroups.value[groupIndex]?.items;
  if (!items) return;
  const next = itemIndex + direction;
  if (next < 0 || next >= items.length) return;
  [items[itemIndex], items[next]] = [items[next], items[itemIndex]];
}

function packageDetailReadyForPublish(): boolean {
  const validityDays = Number(newPackageValidityDays.value);
  const rules = newPackageRules.value;
  const quantityReady = (value: number | string) => {
    const text = String(value).trim();
    return Boolean(text) && (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(text) || Number(text) > 0);
  };
  return Boolean(newPackage.value.name.trim() && newPackageCoverUrl.value
    && Number.isInteger(validityDays) && validityDays >= 1 && validityDays <= 28
    && newPackageContentGroups.value.length > 0
    && newPackageContentGroups.value.every((group) => group.name.trim() && group.items.length > 0 && group.items.every((item) => String(item.name).trim() && quantityReady(item.quantity) && String(item.unit).trim()))
    && [rules.validity, rules.availableTime, rules.reservation, rules.usage, rules.refund].every((value) => value.trim()));
}

function packageDetailsChangedFromLegacy(): boolean {
  if (editingPackageHasStructuredDetails.value) return true;
  if (newPackageContentGroups.value.length > 0 || newPackageValidityDays.value !== '30') return true;
  return (Object.keys(DEFAULT_PACKAGE_RULES) as Array<keyof typeof DEFAULT_PACKAGE_RULES>)
    .some((key) => newPackageRules.value[key] !== DEFAULT_PACKAGE_RULES[key]);
}

async function savePackage(createStatus: 'DRAFT' | 'PUBLISHED' = 'DRAFT') {
  if (packageSaving.value || !newPackage.value.name.trim() || !newPackage.value.price) return;
  if (createStatus === 'PUBLISHED' && !packageDetailReadyForPublish()) {
    const msg = '保存并上架前，请补齐主图、至少一个套餐分组、有效天数和购买须知';
    syncState.value = msg;
    showToast(msg, 'info');
    return;
  }
  packageSaving.value = true;
  try {
    const validityDays = Number(newPackageValidityDays.value);
    const purchaseRules = { ...newPackageRules.value };
    if (purchaseRules.validity === DEFAULT_PACKAGE_RULES.validity && validityDays !== 28) {
      purchaseRules.validity = `购买后 ${validityDays} 天内有效`;
    }
  const detailPayload = {
      contentGroups: newPackageContentGroups.value.map((group) => ({ name: group.name.trim(), items: group.items.map((item) => ({ name: String(item.name).trim(), quantity: item.quantity, unit: String(item.unit).trim(), note: String(item.note ?? '').trim() })) })),
      purchaseRules,
      validityDays,
  };
  if (editingPackageId.value) {
    const basePayload = {
      name: newPackage.value.name.trim(),
      description: newPackage.value.detail.trim(),
      coverUrl: newPackageCoverUrl.value || null,
      priceFen: Number(newPackage.value.price) * 100,
    };
    await apiClient.updatePackage(editingPackageId.value, packageDetailsChangedFromLegacy() ? { ...basePayload, ...detailPayload } : basePayload);
  } else {
    if (createStatus === 'PUBLISHED' && !newPackageCoverUrl.value) {
      uploadState.value = '请先完成套餐主图裁切上传';
      return;
    }
    await apiClient.createPackage({
      name: newPackage.value.name.trim(),
      description: newPackage.value.detail.trim(),
      coverUrl: newPackageCoverUrl.value || undefined,
      priceFen: Number(newPackage.value.price) * 100,
      validUntil: new Date(Date.now() + 30 * 86_400_000).toISOString(),
      status: createStatus,
      ...detailPayload,
    });
    }
    await loadRemoteData();
    const msg = editingPackageId.value ? '套餐修改已保存' : createStatus === 'PUBLISHED' ? '套餐已成功发布上架' : '套餐已保存为草稿';
    if (authenticated.value) syncState.value = msg;
    showToast(msg, 'success');
    showPackageForm.value = false;
    activeTab.value = 'packages';
  } catch (error) {
    const errMsg = `套餐保存失败：${getAdminErrorMessage(error)}`;
    syncState.value = errMsg;
    showToast(errMsg, 'error');
  } finally {
    packageSaving.value = false;
  }
}

async function uploadImage(event: Event, purpose: StoreImagePurpose | 'package-cover') {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  (event.target as HTMLInputElement).value = '';
  const validation = validateImageFile(file);
  if (!validation.ok) {
    uploadState.value = validation.message;
    showToast(uploadState.value, 'error');
    return;
  }
  cropFile.value = file;
  cropPurpose.value = purpose;
  uploadState.value = '请调整裁切范围后确认上传';
}

async function confirmCrop(payload: { blob: Blob; filename: string }) {
  const purpose = cropPurpose.value;
  if (!purpose) return;
  uploadState.value = '正在上传裁切后的图片';
  try {
    const result = await apiClient.uploadMedia(payload.blob, payload.filename, purpose) as { url: string };
    if (purpose === 'package-cover') {
      newPackageCoverUrl.value = result.url;
      uploadState.value = '套餐主图已上传';
      showToast(uploadState.value, 'success');
    } else {
      await apiClient.updateStoreProfile(buildStoreImagePatch(purpose, result.url));
      if (purpose === 'store-cover') {
        storeProfile.value.coverUrl = result.url;
        uploadState.value = '门店封面已自动保存';
      } else {
        storeProfile.value.logoUrl = result.url;
        uploadState.value = '门店 Logo 已自动保存';
      }
      showToast(uploadState.value, 'success');
    }
  } catch (error) {
    uploadState.value = `图片上传或自动保存失败：${getAdminErrorMessage(error)}`;
    showToast(uploadState.value, 'error');
  } finally {
    cropFile.value = null;
    cropPurpose.value = null;
  }
}

function cancelCrop() {
  cropFile.value = null;
  cropPurpose.value = null;
  uploadState.value = '已取消图片裁切';
}

function cropError(error: unknown) {
  cropFile.value = null;
  cropPurpose.value = null;
  const message = error instanceof Error ? error.message : '未知错误';
  uploadState.value = `图片裁切失败：${message}`;
 showToast(uploadState.value, 'error');
}

function canRefund(order: OrderFilterInput & { rawStatus?: string; activeVoucherCount?: number }): boolean {
 const status = order.rawStatus ?? '';
 return ['PAID', 'PARTIALLY_REDEEMED', 'PARTIALLY_REFUNDED'].includes(status) && (order.activeVoucherCount ?? 0) > 0;
}

async function openOrder(order: AdminOrder) {
 selectedOrder.value = { ...order };
 selectedOrderVouchers.value = [];
 orderDetailError.value = '';
 orderDetailLoading.value = true;
 const orderId = order.sourceId ?? (order.id.startsWith('订单-') ? null : order.id);
 if (!orderId) {
  orderDetailError.value = '这条订单缺少真实订单号，暂时无法读取详情';
  orderDetailLoading.value = false;
  return;
 }
 try {
  const [detail, vouchers] = await Promise.all([
   apiClient.getOrder(orderId),
   apiClient.listOrderVouchers(orderId),
  ]);
  if (detail && typeof detail === 'object') selectedOrder.value = { ...selectedOrder.value, ...(detail as Record<string, unknown>), id: orderId };
  if (Array.isArray(vouchers)) selectedOrderVouchers.value = vouchers as Record<string, any>[];
 } catch (error) {
  orderDetailError.value = `订单详情读取失败：${getAdminErrorMessage(error)}`;
  showToast(orderDetailError.value, 'error');
 } finally {
  orderDetailLoading.value = false;
 }
}

function closeOrder() {
 selectedOrder.value = null;
 selectedOrderVouchers.value = [];
 orderDetailError.value = '';
 orderDetailLoading.value = false;
}

async function refundSelectedOrder() {
  if (!selectedOrder.value || !window.confirm('只会退款未核销券，已核销券不会退款。确定继续吗？')) return;
  try {
    const orderId = String(selectedOrder.value.sourceId ?? selectedOrder.value.id ?? '');
    if (!orderId || orderId.startsWith('订单-')) {
      syncState.value = '该订单缺少真实订单号，暂不能退款';
      return;
    }
    const result = await apiClient.refundOrder(orderId) as { refundedCount?: number };
    if (!result.refundedCount) {
      syncState.value = '该订单当前没有可退款的未核销券';
      return;
    }
    syncState.value = `已提交 ${result.refundedCount} 张未核销券退款`;
    await openOrder({ ...(selectedOrder.value as AdminOrder), id: orderId, sourceId: orderId });
    await loadRemoteData();
  } catch (error) {
    syncState.value = getAdminErrorMessage(error);
  }
}

async function saveStoreProfile() {
  storeSaving.value = true;
  try {
    await apiClient.updateStoreProfile(storeProfile.value);
    syncState.value = '门店资料已保存';
    showToast('门店资料与定位已成功保存', 'success');
    mapStatus.value = isValidCoordinate(storeProfile.value.latitude, storeProfile.value.longitude)
      ? '门店位置已保存'
      : mapStatus.value;
  } catch (error) {
    const errMsg = `门店资料保存失败：${getAdminErrorMessage(error)}`;
    syncState.value = errMsg;
    showToast(errMsg, 'error');
  } finally {
    storeSaving.value = false;
  }
}
async function loadOrders() {
 if (!authenticated.value) return;
 orderLoading.value = true;
 orderError.value = '';
 try {
  const remoteOrders = await apiClient.listOrders();
  if (!Array.isArray(remoteOrders)) throw new AdminApiError('INVALID_ORDER_RESPONSE', '订单接口返回的数据不是数组');
  const normalizedOrders = remoteOrders.map((item, index) => normalizeAdminOrder(item, index));
  orders.value = await Promise.all(normalizedOrders.map(async (order) => {
   if (!order.sourceId) return order;
   try {
    const vouchers = await apiClient.listOrderVouchers(order.sourceId) as Array<{ state?: string }>;
    if (!Array.isArray(vouchers)) return order;
    return {
     ...order,
     voucherCount: vouchers.length,
     activeVoucherCount: vouchers.filter((voucher) => voucher?.state === 'ACTIVE').length,
    };
   } catch {
    return order;
   }
  }));
 } catch (error) {
  const code = error instanceof AdminApiError ? error.code : undefined;
  if (code === 'UNAUTHORIZED' || code === 'SESSION_EXPIRED' || code === 'SESSION_INVALID') {
   authError.value = getAdminErrorMessage(error);
   signOut();
   authError.value = getAdminErrorMessage(error);
  } else {
   orderError.value = `订单加载失败：${getAdminErrorMessage(error)}`;
  }
  orders.value = [];
 } finally {
  orderLoading.value = false;
 }
}

async function loadRemoteData() {
 if (!authenticated.value) return;
 syncState.value = '正在同步后台';
 const results = await Promise.allSettled([
  apiClient.listManagePackages(),
  loadOrders(),
  apiClient.listAuditLogs(),
  apiClient.getReport(),
  apiClient.getIntegrationStatus(),
  apiClient.getStoreProfile(),
 ]);
 const [packagesResult, , auditResult, reportResult, integrationResult, storeResult] = results;
 const failures: string[] = [];
 if (packagesResult.status === 'fulfilled') {
  const remotePackages = packagesResult.value;
  if (Array.isArray(remotePackages)) {
   packages.value = remotePackages.map((item: any) => ({
    id: item?.id,
    name: typeof item?.name === 'string' ? item.name : '未命名套餐',
    detail: typeof item?.description === 'string' ? item.description : '',
    contentGroups: item?.contentGroups,
    purchaseRules: item?.purchaseRules,
    validityDays: item?.validityDays,
    coverUrl: item?.coverUrl,
    price: Number.isFinite(Number(item?.priceFen)) ? Number(item.priceFen) / 100 : 0,
    rawStatus: item?.status,
    archivedAt: item?.archivedAt,
    publishedAt: item?.publishedAt,
    canDelete: item?.canDelete,
    status: packageStatusLabel(item?.status, item?.archivedAt),
   }));
  } else failures.push('套餐数据格式异常');
 } else failures.push(`套餐同步失败：${getAdminErrorMessage(packagesResult.reason)}`);
 if (auditResult.status === 'fulfilled') {
  auditLogs.value = Array.isArray(auditResult.value) ? auditResult.value as typeof auditLogs.value : [];
 } else failures.push(`审计日志同步失败：${getAdminErrorMessage(auditResult.reason)}`);
 if (reportResult.status === 'fulfilled' && reportResult.value && typeof reportResult.value === 'object') {
  report.value = reportResult.value as AdminReport;
 } else if (reportResult.status === 'rejected') failures.push(`报表同步失败：${getAdminErrorMessage(reportResult.reason)}`);
 if (integrationResult.status === 'fulfilled' && integrationResult.value && typeof integrationResult.value === 'object') {
  integrationStatus.value = integrationResult.value as IntegrationStatus;
 } else if (integrationResult.status === 'rejected') failures.push(`接入状态同步失败：${getAdminErrorMessage(integrationResult.reason)}`);
 if (storeResult.status === 'fulfilled' && storeResult.value && typeof storeResult.value === 'object') {
  storeProfile.value = { ...storeProfile.value, ...(storeResult.value as Partial<StoreProfile>) };
  if (activeTab.value === 'settings') void nextTick(initializeTencentMap);
 } else if (storeResult.status === 'rejected') failures.push(`门店资料同步失败：${getAdminErrorMessage(storeResult.reason)}`);
 if (orderError.value) failures.push(orderError.value);
 syncState.value = failures.length > 0 ? `后台已连接，${failures[0]}` : '已连接后台';
}

onMounted(() => { if (authenticated.value) void loadRemoteData(); });
onBeforeUnmount(() => destroyTencentMap());
</script>

<template>
  <div v-if="!authenticated" class="admin-login">
    <div class="admin-login-card">
      <div class="brand-mark">贝</div>
      <span class="eyebrow">小贝壳海鲜啤酒屋</span>
      <h1>后台管理</h1>
      <p>请输入门店统一口令，登录后查看真实经营数据。</p>
      <form @submit.prevent="signIn">
        <label>统一口令<input v-model="authCode" type="password" inputmode="numeric" autocomplete="current-password" placeholder="请输入口令" /></label>
        <p v-if="authError" class="auth-error">{{ authError }}</p>
        <button class="primary-button" :disabled="authSubmitting">{{ authSubmitting ? '登录中…' : '进入管理后台' }}</button>
      </form>
    </div>
  </div>
  <div v-else class="app-shell">
    <aside class="sidebar">
      <div class="brand">
        <div class="brand-mark">贝</div>
        <div><strong>小贝壳</strong><span>团购运营台</span></div>
      </div>
      <div class="store-switch"><span class="online-dot" />{{ tenantName }}<b>⌄</b></div>
      <nav>
        <button v-for="tab in tabs" :key="tab.id" :class="{ active: activeTab === tab.id }" @click="activeTab = tab.id">
          <span class="nav-icon">{{ tab.icon }}</span>{{ tab.label }}
        </button>
      </nav>
      <div class="sidebar-footer"><span class="avatar">店</span><div><strong>门店管理员</strong><small>当前门店全部权限</small></div><button class="sign-out" @click="signOut">退出</button></div>
    </aside>

    <main class="main-content">
      <header class="topbar"><div><span class="eyebrow">{{ tenantName }}</span><h1>{{ currentTitle }}</h1></div><div class="top-actions"><span class="sync-state">{{ syncState }}</span><button class="user-chip"><span class="avatar small">店</span>门店管理员</button></div></header>

      <section v-if="activeTab === 'overview'" class="page-content overview-page">
        <div class="welcome"><div><span class="eyebrow light">实时门店数据</span><h2>欢迎回来，开始管理今天的团购。</h2><p>以下数据来自当前门店后台，暂无数据时会如实显示为空。</p></div><div class="wave">〰</div></div>
        <div class="metrics"><article><span>已支付成交额</span><strong>¥ {{ metricSummary.amount.toFixed(2) }}</strong><small class="muted">按当前同步订单计算</small></article><article><span>已支付订单</span><strong>{{ metricSummary.orderCount }}</strong><small class="muted">租户范围内</small></article><article><span>待核销订单</span><strong>{{ metricSummary.pendingVouchers }}</strong><small class="muted">以券状态为准</small></article><article><span>审计记录</span><strong>{{ auditLogs.length }}</strong><small class="muted">后台操作可追溯</small></article></div>
        <div class="content-grid"><section class="panel trend-panel"><div class="panel-heading"><div><span class="eyebrow">SALES OVERVIEW</span><h3>成交趋势</h3></div></div><div class="empty-state">趋势数据将在产生真实订单后显示</div></section><section class="panel quick-panel"><div class="panel-heading"><div><span class="eyebrow">QUICK ACTIONS</span><h3>常用操作</h3></div></div><button class="action-card" @click="openNewPackage"><span class="action-icon teal">＋</span><span><strong>新建团购套餐</strong><small>创建并保存一个真实套餐</small></span><b>›</b></button><button class="action-card" @click="activeTab = 'orders'; orderFilter = 'PENDING_REDEEM'"><span class="action-icon amber">⌁</span><span><strong>处理待核销券</strong><small>查看当前真实券状态</small></span><b>›</b></button></section></div>
        <section class="panel recent-panel"><div class="panel-heading"><div><span class="eyebrow">RECENT ORDERS</span><h3>最近订单</h3></div><button class="text-button" @click="activeTab = 'orders'">查看全部订单 →</button></div><div class="order-list"><button v-for="order in orders" :key="order.id" class="order-row" @click="openOrder(order)"><span class="order-code">{{ order.id }}</span><span class="order-buyer">{{ order.buyer }}</span><span class="order-item">{{ order.item }}</span><strong>¥{{ order.amount }}</strong><span :class="['status-pill', order.rawStatus === 'REFUNDED' || order.rawStatus === 'PARTIALLY_REFUNDED' ? 'danger' : order.rawStatus === 'FULLY_REDEEMED' ? 'completed' : order.rawStatus === 'PAID' || order.rawStatus === 'PARTIALLY_REDEEMED' ? 'info' : 'success']">{{ order.status }}</span><span class="order-time">{{ order.time }}</span></button></div></section>
      </section>

      <section v-else class="page-content inner-page">
        <div v-if="activeTab === 'packages'" class="page-toolbar"><div><span class="eyebrow">PACKAGE MANAGEMENT</span><h2>团购套餐</h2></div><button class="primary-button" @click="openNewPackage">＋ 新建团购套餐</button></div>
        <div v-if="activeTab === 'packages'" class="package-page"><div class="sub-filter-row"><button :class="['filter', { active: packageView === 'ACTIVE' }]" @click="packageView = 'ACTIVE'">经营中</button><button :class="['filter', { active: packageView === 'ARCHIVED' }]" @click="packageView = 'ARCHIVED'">已归档</button></div><div class="package-grid"><article v-for="item in visiblePackages" :key="item.id || item.name" class="package-card"><div class="package-cover"><img v-if="item.coverUrl" :src="item.coverUrl" :alt="item.name" /><span v-else>海鲜</span><small>{{ item.status }}</small></div><div class="package-body"><h3>{{ item.name }}</h3><p>{{ item.detail }}</p><div class="package-meta"><strong>¥{{ item.price }}</strong><span>固定套餐清单</span></div><div class="package-actions"><button class="row-action" @click.stop="openEditPackage(item)">编辑</button><button v-if="!item.archivedAt" class="row-action" @click.stop="togglePackage(item)">{{ packageStatusForApi(item) === 'PUBLISHED' ? '下架' : '上架' }}</button><button v-if="item.archivedAt" class="row-action" @click.stop="restorePackage(item)">恢复</button><button v-if="item.canDelete" class="row-action danger-text" @click.stop="deletePackage(item)">删除</button><button v-else-if="!item.archivedAt" class="row-action" @click.stop="archivePackage(item)">归档</button></div></div></article><div v-if="visiblePackages.length === 0" class="empty-state"><template v-if="packageView === 'ARCHIVED'">暂无已归档套餐</template><template v-else><p>暂无经营中的套餐</p><button type="button" class="primary-button" @click="openNewPackage">新建第一个套餐</button></template></div></div></div>
<section v-else-if="activeTab === 'orders'" class="panel table-panel">
 <div class="filter-row"><button :class="['filter', { active: orderFilter === 'ALL' }]" @click="orderFilter = 'ALL'">全部订单</button><button :class="['filter', { active: orderFilter === 'PENDING_REDEEM' }]" @click="orderFilter = 'PENDING_REDEEM'">待核销</button><button :class="['filter', { active: orderFilter === 'REDEEMED' }]" @click="orderFilter = 'REDEEMED'">已核销</button><button :class="['filter', { active: orderFilter === 'REFUND' }]" @click="orderFilter = 'REFUND'">退款</button><span class="filter-spacer" /><input v-model="orderSearch" placeholder="搜索订单号或用户" /></div>
 <div v-if="orderLoading" class="empty-state order-state">正在读取真实订单…</div>
 <div v-else-if="orderError" class="empty-state order-state order-error-state"><strong>订单暂时无法加载</strong><span>{{ orderError }}</span><button type="button" class="outline-button" @click="loadOrders">重新加载</button></div>
 <div v-else class="table"><div class="table-head"><span>订单号</span><span>顾客</span><span>套餐</span><span>金额</span><span>状态</span><span>时间</span><span>操作</span></div><button v-for="order in visibleOrders" :key="order.id" class="table-row" @click="openOrder(order)"><span class="order-code">{{ order.id }}</span><span>{{ order.buyer }}</span><span>{{ order.item }}</span><strong>¥{{ order.amount.toFixed(2) }}</strong><span :class="['status-pill', order.rawStatus === 'REFUNDED' || order.rawStatus === 'PARTIALLY_REFUNDED' ? 'danger' : order.rawStatus === 'PAID' || order.rawStatus === 'PARTIALLY_REDEEMED' ? 'info' : 'success']">{{ order.status }}</span><span>{{ order.time }}</span><span class="row-actions"><span class="row-action">查看</span><span v-if="canRefund(order)" class="row-action danger-text" @click.stop="openOrder(order).then(() => refundSelectedOrder())">退款</span></span></button><div v-if="visibleOrders.length === 0" class="empty-state">暂无符合条件的真实订单</div></div>
</section>
        <section v-else-if="activeTab === 'audit'" class="panel table-panel"><div class="table"><div class="table-head"><span>时间</span><span>动作</span><span>目标</span></div><div v-for="log in auditLogs" :key="log.id" class="table-row"><span>{{ new Date(log.createdAt).toLocaleString('zh-CN') }}</span><span>{{ log.action }}</span><span class="order-code">{{ log.targetId }}</span></div><div v-if="auditLogs.length === 0" class="empty-state">暂无审计记录</div></div></section>
        <section v-else-if="activeTab === 'settings'" class="settings-page"><section class="panel store-form"><div class="panel-heading"><div><span class="eyebrow">STORE PROFILE</span><h3>门店资料与首页素材</h3></div><button class="primary-button" :disabled="storeSaving" @click="saveStoreProfile">{{ storeSaving ? '保存中…' : '保存资料' }}</button></div><div class="store-image-grid"><label class="image-upload"><span>门店封面</span><img v-if="storeProfile.coverUrl" :src="storeProfile.coverUrl" alt="门店封面预览" /><span v-else class="image-placeholder">＋<small>上传封面</small></span><input type="file" accept="image/jpeg,image/png,image/webp" @change="uploadImage($event, 'store-cover')" /></label><label class="image-upload logo-upload"><span>门店 Logo</span><img v-if="storeProfile.logoUrl" :src="storeProfile.logoUrl" alt="门店 Logo 预览" /><span v-else class="image-placeholder">＋<small>上传 Logo</small></span><input type="file" accept="image/jpeg,image/png,image/webp" @change="uploadImage($event, 'store-logo')" /></label></div><div class="form-grid"><label>门店名称<input v-model="storeProfile.name" /></label><label>联系电话<input v-model="storeProfile.phone" placeholder="0532-xxxxxxx" /></label><label>营业时间<input v-model="storeProfile.businessHours" placeholder="17:00-02:00" /></label><label>门店地址<input v-model="storeProfile.address" /></label><label>纬度<input v-model.number="storeProfile.latitude" type="number" step="any" placeholder="例如 36.0671" /></label><label>经度<input v-model.number="storeProfile.longitude" type="number" step="any" placeholder="例如 120.3826" /></label><label class="wide-field">到店须知<textarea v-model="storeProfile.notice" rows="3" placeholder="请提前出示团购券…" /></label></div><section class="map-picker-panel"><div class="panel-heading"><div><span class="eyebrow">STORE LOCATION</span><h3>腾讯地图选点</h3></div><span v-if="mapLoading" class="setting-state pending-text">地图加载中</span></div><div v-if="tencentMapKey" class="map-search-row"><input v-model="mapSearchAddress" placeholder="输入地址后搜索，例如门店完整地址" @keyup.enter="searchStoreAddress" /><button type="button" class="outline-button" @click="searchStoreAddress">搜索地址</button></div><div v-if="tencentMapKey" ref="mapContainer" class="store-map"></div><p class="map-status">{{ mapStatus || (tencentMapKey ? '请搜索地址、点击地图或拖动标记选择真实门店位置' : '腾讯地图未配置，可直接填写经纬度') }}</p></section><p v-if="uploadState" class="upload-state">{{ uploadState }}</p></section><section class="settings-grid"><article class="panel setting-card"><div :class="['setting-icon', integrationStatus.payment.ready ? 'ready' : 'pending']">￥</div><div><span class="eyebrow">WECHAT PAY</span><h3>微信支付</h3><p>服务商模式 · {{ integrationStatus.payment.mode }} · 配置门禁状态</p></div><span :class="['setting-state', integrationStatus.payment.ready ? 'ready-text' : 'pending-text']">{{ integrationStatus.payment.ready ? '已就绪' : '待配置' }}</span><button class="outline-button">查看配置门禁</button></article><article class="panel setting-card"><div :class="['setting-icon', integrationStatus.persistence.mode === 'prisma' ? 'ready' : 'pending']">▣</div><div><span class="eyebrow">RUNTIME</span><h3>数据与维护</h3><p>持久化 {{ integrationStatus.persistence.mode }} · 独立 worker {{ integrationStatus.worker.enabled ? '已启用' : '未启用' }}</p></div><span :class="['setting-state', integrationStatus.persistence.mode === 'prisma' && integrationStatus.worker.enabled ? 'ready-text' : 'pending-text']">{{ integrationStatus.persistence.mode === 'prisma' && integrationStatus.worker.enabled ? '已就绪' : '待配置' }}</span><button class="outline-button">查看运行门禁</button></article></section></section>
      </section>
    </main>

    <Transition name="modal">
      <div v-if="showPackageForm" class="modal-backdrop" @click.self="showPackageForm = false">
        <form class="modal package-editor-modal" @submit.prevent="savePackage()">
          <span class="eyebrow">{{ editingPackageId ? 'EDIT PACKAGE' : 'NEW PACKAGE' }}</span>
          <h2>{{ editingPackageId ? '编辑团购套餐' : '新建团购套餐' }}</h2>
          <label>套餐名称<input v-model="newPackage.name" required placeholder="例如：双人海鲜啤酒套餐" /></label>
          <label>一句话说明（选填）<input v-model="newPackage.detail" placeholder="顾客在小程序看到的说明" /></label>
          <div class="form-grid compact"><label>单份价格（元）<input v-model="newPackage.price" required inputmode="decimal" placeholder="168" /></label><label>购买后有效天数<input v-model="newPackageValidityDays" type="number" min="1" max="28" /></label></div>
          <section class="detail-editor-section">
            <div class="editor-section-heading"><div><span class="eyebrow">PACKAGE CONTENT</span><h3>套餐内容</h3></div><button type="button" class="outline-button" @click="addContentGroup">＋ 新增分组</button></div>
            <article v-for="(group, groupIndex) in newPackageContentGroups" :key="groupIndex" class="content-group-editor">
  <div class="content-group-heading"><input v-model="group.name" placeholder="分组名称，例如：海鲜" /><div class="inline-actions"><button type="button" class="package-icon-action" @click="moveContentGroup(groupIndex, -1)">↑</button><button type="button" class="package-icon-action" @click="moveContentGroup(groupIndex, 1)">↓</button><button type="button" class="package-icon-action danger-text" @click="removeContentGroup(groupIndex)">删</button></div></div>
  <div v-for="(entry, itemIndex) in group.items" :key="itemIndex" class="content-item-editor"><input v-model="entry.name" placeholder="项目名称" /><input v-model="entry.quantity" placeholder="数量" /><input v-model="entry.unit" placeholder="单位" /><input v-model="entry.note" placeholder="备注（可选）" /><div class="inline-actions"><button type="button" class="package-icon-action" @click="moveContentItem(groupIndex, itemIndex, -1)">↑</button><button type="button" class="package-icon-action" @click="moveContentItem(groupIndex, itemIndex, 1)">↓</button><button type="button" class="package-icon-action danger-text" @click="removeContentItem(groupIndex, itemIndex)">删</button></div></div>
              <button type="button" class="text-button" @click="addContentItem(groupIndex)">＋ 添加项目</button>
            </article>
            <div v-if="newPackageContentGroups.length === 0" class="empty-state compact-empty">还没有内容分组，请先新增一个分组</div>
          </section>
          <section class="detail-editor-section">
            <div class="editor-section-heading"><div><span class="eyebrow">PURCHASE RULES</span><h3>购买须知</h3></div><span class="muted">规则会固定展示在详情页</span></div>
          <div class="rules-grid"><label>有效期<input v-model="newPackageRules.validity" /></label><label>可用时间<input v-model="newPackageRules.availableTime" /></label><label>预约规则<input v-model="newPackageRules.reservation" /></label><label>使用方式<input v-model="newPackageRules.usage" /></label><label>退款规则<input v-model="newPackageRules.refund" /></label><label class="wide-field">其他说明<textarea v-model="newPackageRules.other" rows="2" placeholder="按需填写" /></label></div>
          </section>
          <label class="file-picker">套餐主图（16:9）<input type="file" accept="image/jpeg,image/png,image/webp" @change="uploadImage($event, 'package-cover')" /><span>选择图片后会打开裁切窗口，输出为 1600×900</span></label>
          <img v-if="newPackageCoverUrl" class="package-form-preview" :src="newPackageCoverUrl" alt="套餐主图预览" />
          <small v-else class="package-image-empty">待裁切上传</small>
          <p v-if="uploadState" class="upload-state">{{ uploadState }}</p>
<div class="package-mobile-preview"><div class="phone-preview"><div class="phone-preview-media"><img v-if="newPackageCoverUrl" :src="newPackageCoverUrl" alt="详情主图预览" /><span v-else>详情主图</span></div><div class="phone-preview-body"><strong>{{ newPackage.name || '套餐名称' }}</strong><span class="preview-price">¥{{ newPackage.price || '0' }} <small>/ 份</small></span><p v-if="newPackage.detail.trim()">{{ newPackage.detail }}</p><h4>团购详情</h4><div v-for="group in newPackageContentGroups" :key="group.name" class="preview-group"><b>{{ group.name || '未命名分组' }}</b><span v-for="entry in group.items" :key="entry.name">{{ entry.name || '未命名项目' }} {{ entry.quantity }}{{ entry.unit }}</span></div><h4>购买须知</h4><span>{{ newPackageRules.validity || '有效期' }}</span><span>{{ newPackageRules.availableTime || '可用时间' }}</span></div></div></div>
          <div class="modal-actions"><button type="button" class="outline-button" @click="showPackageForm = false">取消</button><template v-if="editingPackageId"><button type="submit" class="primary-button" :disabled="packageSaving">{{ packageSaving ? '保存中…' : '保存修改' }}</button></template><template v-else><button type="button" class="outline-button" :disabled="packageSaving" @click="savePackage('DRAFT')">{{ packageSaving ? '保存中…' : '保存草稿' }}</button><button type="button" class="primary-button" :disabled="packageSaving" @click="savePackage('PUBLISHED')">{{ packageSaving ? '保存中…' : '保存并上架' }}</button></template></div>
        </form>
      </div>
    </Transition>
    <OrderDetailDrawer :order="selectedOrder" :vouchers="selectedOrderVouchers" :loading="orderDetailLoading" :error="orderDetailError" :can-refund="selectedOrder ? canRefund({ rawStatus: selectedOrder.rawStatus, activeVoucherCount: selectedOrderVouchers.filter((voucher) => voucher.state === 'ACTIVE').length } as AdminOrder) : false" @close="closeOrder" @refund="refundSelectedOrder" />
    <ImageCropDialog :open="Boolean(cropFile && cropPurpose)" :file="cropFile" :aspect-ratio="cropAspectRatio" :output-width="cropOutputWidth" :output-height="cropOutputHeight" :output-mime-type="cropOutputMimeType" @cancel="cancelCrop" @confirm="confirmCrop" @error="cropError" />

    <!-- 全局 Confirm 确认弹窗 -->
    <Transition name="modal">
      <div v-if="confirmDialog.show" class="modal-backdrop" @click.self="confirmDialog.show = false">
        <div class="modal confirm-modal">
          <div :class="['confirm-icon', confirmDialog.danger ? 'danger' : 'info']">
            {{ confirmDialog.danger ? '!' : '?' }}
          </div>
          <h2>{{ confirmDialog.title }}</h2>
          <p class="confirm-message">{{ confirmDialog.message }}</p>
          <div class="modal-actions center-actions">
            <button type="button" class="outline-button" @click="confirmDialog.show = false">取消</button>
            <button type="button" :class="confirmDialog.danger ? 'danger-button' : 'primary-button'" @click="handleConfirmExecute">
              {{ confirmDialog.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 全局 Toast 消息队列 -->
    <div class="toast-container">
      <TransitionGroup name="toast">
        <div v-for="toast in toasts" :key="toast.id" :class="['toast-item', toast.type]">
          <span class="toast-icon">
            <template v-if="toast.type === 'success'">✓</template>
            <template v-else-if="toast.type === 'error'">✕</template>
            <template v-else>ℹ</template>
          </span>
          <span class="toast-msg">{{ toast.message }}</span>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>
